import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import AssessmentHistory from "../components/AssessmentHistory";
import { api } from "../lib/api";
import {
  Barbell,
  Calculator,
  CheckCircle,
  FloppyDisk,
  Heartbeat,
  Lightning,
  Sparkle,
  Target,
  ArrowRight,
} from "@phosphor-icons/react";

const LIFTS = [
  { key: "squat_1rm_lb", label: "Back Squat", icon: Barbell },
  { key: "bench_1rm_lb", label: "Bench Press", icon: Barbell },
  { key: "deadlift_1rm_lb", label: "Deadlift", icon: Barbell },
  { key: "ohp_1rm_lb", label: "Overhead Press", icon: Barbell },
];

// Epley formula: 1RM = weight × (1 + reps/30). Best for 2–10 rep sets.
function epley1RM(weight, reps) {
  if (!weight || !reps) return null;
  if (reps === 1) return Math.round(weight);
  return Math.round(weight * (1 + reps / 30));
}

function LiftRow({ lift, value, onChange, onCalc }) {
  const [calcOpen, setCalcOpen] = useState(false);
  const [w, setW] = useState("");
  const [r, setR] = useState("");
  const estimate = w && r ? epley1RM(parseFloat(w), parseInt(r)) : null;

  const apply = () => {
    if (estimate) {
      onCalc(estimate);
      setCalcOpen(false);
      setW("");
      setR("");
    }
  };

  return (
    <div className="border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <lift.icon size={18} weight="bold" className="text-volt" />
          <div className="font-display text-lg font-black uppercase">{lift.label}</div>
        </div>
        <button
          onClick={() => setCalcOpen((o) => !o)}
          data-testid={`calc-toggle-${lift.key}`}
          className="text-xs uppercase tracking-widest font-display text-muted-foreground hover:text-volt inline-flex items-center gap-1"
        >
          <Calculator size={14} weight="bold" /> {calcOpen ? "Hide" : "Estimate"}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
          placeholder="Tested 1RM"
          className="flex-1 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt"
          data-testid={`lift-${lift.key}-input`}
        />
        <span className="label-mono text-muted-foreground">LB</span>
      </div>

      {calcOpen && (
        <div className="mt-4 pt-4 border-t border-border space-y-3">
          <div className="label-mono text-muted-foreground">// REP-MAX CALCULATOR (Epley)</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <input
                type="number"
                value={w}
                onChange={(e) => setW(e.target.value)}
                placeholder="Weight"
                className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-volt"
                data-testid={`calc-weight-${lift.key}`}
              />
              <div className="label-mono mt-1">LB LIFTED</div>
            </div>
            <div>
              <input
                type="number"
                value={r}
                onChange={(e) => setR(e.target.value)}
                placeholder="Reps"
                min="1"
                max="15"
                className="w-full bg-background border border-border px-3 py-2 focus:outline-none focus:border-volt"
                data-testid={`calc-reps-${lift.key}`}
              />
              <div className="label-mono mt-1">REPS PERFORMED</div>
            </div>
          </div>
          {estimate && (
            <div className="flex items-center justify-between bg-secondary border border-volt p-3">
              <div>
                <div className="label-mono text-volt">// ESTIMATED 1RM</div>
                <div className="font-display text-2xl font-black">{estimate} <span className="text-muted-foreground text-sm font-normal tracking-widest">LB</span></div>
              </div>
              <button
                onClick={apply}
                data-testid={`calc-apply-${lift.key}`}
                className="bg-volt text-black px-4 py-2 font-display uppercase tracking-widest text-xs hover:bg-white"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NumberField({ label, suffix, value, onChange, testid, placeholder = "" }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
          placeholder={placeholder}
          className="flex-1 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt"
          data-testid={testid}
        />
        {suffix && <span className="label-mono text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

function TimeField({ label, value, onChange, testid, mode }) {
  // mode "min:sec" → total seconds. value is seconds.
  const total = value ?? null;
  const m = total != null ? Math.floor(total / 60) : "";
  const s = total != null ? total % 60 : "";

  const set = (mins, secs) => {
    const mm = parseInt(mins) || 0;
    const ss = parseInt(secs) || 0;
    if (!mins && !secs) onChange(null);
    else onChange(mm * 60 + ss);
  };

  if (mode === "min:sec") {
    return (
      <div>
        <div className="label-mono mb-1">{label}</div>
        <div className="flex items-center gap-2">
          <input type="number" value={m} onChange={(e) => set(e.target.value, s || 0)} placeholder="mm"
                 className="w-20 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt" data-testid={`${testid}-min`} />
          <span className="label-mono">:</span>
          <input type="number" value={s} onChange={(e) => set(m || 0, e.target.value)} placeholder="ss" min="0" max="59"
                 className="w-20 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt" data-testid={`${testid}-sec`} />
          <span className="label-mono text-muted-foreground">MM:SS</span>
        </div>
      </div>
    );
  }

  if (mode === "hr:min:sec") {
    // 12-mile ruck typically lands 2:00–4:00 — hours field is essential.
    const h = total != null ? Math.floor(total / 3600) : "";
    const mm = total != null ? Math.floor((total % 3600) / 60) : "";
    const ss = total != null ? total % 60 : "";
    const setHMS = (hh, mins, secs) => {
      const H = parseInt(hh) || 0;
      const M = parseInt(mins) || 0;
      const S = parseInt(secs) || 0;
      if (!hh && !mins && !secs) onChange(null);
      else onChange(H * 3600 + M * 60 + S);
    };
    return (
      <div>
        <div className="label-mono mb-1">{label}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="number" value={h} onChange={(e) => setHMS(e.target.value, mm || 0, ss || 0)} placeholder="hh" min="0" max="6"
                 className="w-16 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt" data-testid={`${testid}-hr`} />
          <span className="label-mono">:</span>
          <input type="number" value={mm} onChange={(e) => setHMS(h || 0, e.target.value, ss || 0)} placeholder="mm" min="0" max="59"
                 className="w-16 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt" data-testid={`${testid}-min`} />
          <span className="label-mono">:</span>
          <input type="number" value={ss} onChange={(e) => setHMS(h || 0, mm || 0, e.target.value)} placeholder="ss" min="0" max="59"
                 className="w-16 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt" data-testid={`${testid}-sec`} />
          <span className="label-mono text-muted-foreground">HH:MM:SS</span>
        </div>
      </div>
    );
  }

  // raw seconds
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input type="number" value={total ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value))}
               className="flex-1 bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt" data-testid={testid} />
        <span className="label-mono text-muted-foreground">SEC</span>
      </div>
    </div>
  );
}

export default function Assessment() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    body_weight_lb: null,
    squat_1rm_lb: null,
    bench_1rm_lb: null,
    deadlift_1rm_lb: null,
    ohp_1rm_lb: null,
    max_pushups: null,
    max_pullups: null,
    plank_seconds: null,
    two_mile_run_seconds: null,
    twelve_mile_ruck_seconds: null,
    notes: "",
  });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/me/assessment").then((r) => {
      const d = r.data || {};
      setData((prev) => ({ ...prev, ...d, notes: d.notes || "" }));
      setUpdatedAt(d.updated_at || null);
    }).catch(() => {});
  }, []);

  const [historyVersion, setHistoryVersion] = useState(0);

  const setField = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true); setErr(""); setSaved(false);
    try {
      const payload = {
        body_weight_lb: data.body_weight_lb,
        squat_1rm_lb: data.squat_1rm_lb,
        bench_1rm_lb: data.bench_1rm_lb,
        deadlift_1rm_lb: data.deadlift_1rm_lb,
        ohp_1rm_lb: data.ohp_1rm_lb,
        max_pushups: data.max_pushups,
        max_pullups: data.max_pullups,
        plank_seconds: data.plank_seconds,
        two_mile_run_seconds: data.two_mile_run_seconds,
        twelve_mile_ruck_seconds: data.twelve_mile_ruck_seconds,
        notes: data.notes || null,
      };
      const { data: saved } = await api.put("/me/assessment", payload);
      setUpdatedAt(saved.updated_at);
      setSaved(true);
      setHistoryVersion((v) => v + 1);  // refresh history table
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const fieldsCompleted = [
    data.body_weight_lb, data.squat_1rm_lb, data.bench_1rm_lb, data.deadlift_1rm_lb,
    data.ohp_1rm_lb, data.max_pushups, data.max_pullups, data.plank_seconds, data.two_mile_run_seconds,
  ].filter((v) => v != null && v !== "").length;
  const totalFields = 9;

  return (
    <Layout>
      <div className="mb-8">
        <div className="label-mono text-volt mb-1">// FIELD MANUAL // SECTION 2</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black">ASSESSMENT</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Log your 1-rep maxes and bodyweight benchmarks. The AI Coach uses these to scale every weight prescription off your real numbers — not generic guesses.
        </p>
      </div>

      {/* Status bar */}
      <div className="grid sm:grid-cols-3 gap-px bg-border border border-border mb-10">
        <div className="bg-background p-5">
          <div className="label-mono text-volt mb-1">// COMPLETION</div>
          <div className="font-display text-3xl font-black" data-testid="assessment-completion">
            {fieldsCompleted} <span className="text-muted-foreground text-base font-normal">/ {totalFields}</span>
          </div>
          <div className="h-1 bg-border mt-2">
            <div className="h-full bg-volt transition-all" style={{ width: `${(fieldsCompleted / totalFields) * 100}%` }} />
          </div>
        </div>
        <div className="bg-background p-5">
          <div className="label-mono text-volt mb-1">// LAST UPDATED</div>
          <div className="font-display text-base font-bold">
            {updatedAt ? new Date(updatedAt).toLocaleString() : "Never"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Update every 4–8 weeks for accurate prescriptions.</div>
        </div>
        <div className="bg-background p-5 flex items-center justify-between">
          <div>
            <div className="label-mono text-volt mb-1">// USE IT</div>
            <div className="font-display text-base font-bold">DRAFT A PLAN ↓</div>
          </div>
          <Link
            to="/ai"
            data-testid="assessment-go-ai"
            className="bg-volt text-black px-3 py-2 inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs hover:bg-white"
          >
            <Sparkle size={14} weight="bold" /> AI Coach <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>

      {/* Main lifts */}
      <section className="mb-12">
        <div className="label-mono mb-2">// MAIN LIFTS</div>
        <h2 className="font-display text-2xl sm:text-3xl font-black mb-6">THE BIG FOUR</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl text-sm">
          Don't have a tested 1RM? Click "Estimate" and the Epley formula calculates your 1RM from a heavy 2–10 rep set.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {LIFTS.map((l) => (
            <LiftRow
              key={l.key}
              lift={l}
              value={data[l.key]}
              onChange={setField(l.key)}
              onCalc={setField(l.key)}
            />
          ))}
        </div>
      </section>

      {/* Bodyweight benchmarks */}
      <section className="mb-12">
        <div className="label-mono mb-2">// BENCHMARKS</div>
        <h2 className="font-display text-2xl sm:text-3xl font-black mb-6">BODYWEIGHT &amp; CONDITIONING</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 border border-border p-5 bg-card">
          <NumberField
            label="Body Weight"
            suffix="LB"
            value={data.body_weight_lb}
            onChange={setField("body_weight_lb")}
            testid="bw-input"
            placeholder="180"
          />
          <NumberField
            label="Max Push-Ups (HRP)"
            suffix="REPS"
            value={data.max_pushups}
            onChange={setField("max_pushups")}
            testid="pushups-input"
            placeholder="55"
          />
          <NumberField
            label="Max Pull-Ups"
            suffix="REPS"
            value={data.max_pullups}
            onChange={setField("max_pullups")}
            testid="pullups-input"
            placeholder="12"
          />
          <TimeField
            label="Plank Hold"
            value={data.plank_seconds}
            onChange={setField("plank_seconds")}
            testid="plank"
            mode="raw"
          />
          <TimeField
            label="2-Mile Run"
            value={data.two_mile_run_seconds}
            onChange={setField("two_mile_run_seconds")}
            testid="run-2mr"
            mode="min:sec"
          />
          <TimeField
            label="12-Mile Ruck (35 lb)"
            value={data.twelve_mile_ruck_seconds}
            onChange={setField("twelve_mile_ruck_seconds")}
            testid="ruck-12mi"
            mode="hr:min:sec"
          />
        </div>
      </section>

      {/* Notes + save */}
      <section className="mb-10">
        <div className="label-mono mb-2">// CONTEXT</div>
        <textarea
          value={data.notes || ""}
          onChange={(e) => setData((d) => ({ ...d, notes: e.target.value }))}
          rows="3"
          placeholder="Any limitations, recent injuries, or context the AI should know about..."
          className="w-full bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt"
          data-testid="assessment-notes"
        />
      </section>

      {/* Improvement history */}
      <section className="mb-10">
        <AssessmentHistory key={historyVersion} mode="self" />
      </section>

      <div className="flex flex-wrap items-center gap-3 sticky bottom-0 bg-background py-4 border-t border-border">
        <button
          onClick={save}
          disabled={saving}
          className="bg-volt text-black px-6 py-3 font-display uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="assessment-save"
        >
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving..." : "Save Assessment"}
        </button>
        {saved && (
          <span className="inline-flex items-center gap-2 text-volt label-mono" data-testid="assessment-saved-banner">
            <CheckCircle size={16} weight="bold" /> SAVED — AI WILL USE THESE NUMBERS
          </span>
        )}
        {err && <span className="border border-destructive text-destructive px-3 py-2 text-sm">{err}</span>}
      </div>
    </Layout>
  );
}
